// Planejar o trajeto (0.22.0): as contas do planejador, puras (testadas em
// test/app/trajeto.test.js). O servidor (planejar_trajeto, migration 25)
// devolve o pedaço do mapa: as regiões imediatas do IBGE, os trechos entre
// elas (as cargas por mês que a carroceria leva, pelo CIOT da ANTT) e os
// fretes da empresa no último ano. Aqui ficam o preço de cada trecho (o piso
// da ANTT ajustado pelo que a empresa recebe), o custo (o mesmo do Raio-X), o
// tempo (rodando, carregando e esperando a carga), a chance de achar a carga
// e a nota de cada trajeto, pelo lucro por dia (não pela menor distância).
// 0.22.1: a carga que sai a granel ou ensacada (arroz, açúcar, adubo…) conta
// para a grade baixa, com aviso; e o frete de retorno do Norte e do Nordeste
// conta uma parte do piso (70%, a empresa muda), com aviso de risco.
import { pisoMinimo } from './antt.js';
import { FATOR_DA_ESTRADA, kmEntre, kmEstimado } from './geo.js';

// O que o caminhão pode levar (o cadastro dele) e os grupos de carga que o
// robô do radar tira do NCM (scripts/radar.py, GRUPO_DO_NCM).
export const CARROCERIAS = [
  ['graneleira', 'Graneleira'],
  ['cacamba', 'Caçamba (basculante)'],
  ['carga_seca', 'Grade baixa / carga seca / sider'],
  ['bau', 'Baú'],
  ['frigorifica', 'Frigorífica'],
  ['tanque', 'Tanque'],
  ['porta_conteiner', 'Porta-contêiner'],
  ['cegonha', 'Cegonha'],
  ['boiadeira', 'Boiadeira'],
  ['outra', 'Outra (prancha…)'],
];

// 'ensacavel': arroz, açúcar, adubo, sal, cal e gesso, que saem a granel ou
// ensacados (o CIOT não diz qual). A graneleira leva dos dois jeitos; a
// caçamba e a grade baixa, de um só (a tela avisa).
export const GRUPOS_DA_CARROCERIA = {
  graneleira: ['granel', 'ensacavel', 'geral'],
  cacamba: ['granel', 'ensacavel'],
  carga_seca: ['geral', 'ensacavel'],
  bau: ['geral'],
  frigorifica: ['frio', 'geral'],
  tanque: ['liquido'],
  porta_conteiner: ['geral'],
  cegonha: ['veiculo'],
  boiadeira: ['animais'],
  outra: ['geral'],
};

// O tipo de carga do piso da ANTT de cada grupo.
export const TIPO_DO_GRUPO = {
  granel: 'granel_solido',
  ensacavel: 'carga_geral', // na graneleira e na caçamba, granel sólido (tipoDoFrete)
  liquido: 'granel_liquido',
  frio: 'frigorificada',
  geral: 'carga_geral',
  veiculo: 'carga_geral',
  animais: 'carga_geral',
};

export const NOME_DO_GRUPO = {
  granel: 'granel',
  ensacavel: 'granel ou ensacada',
  liquido: 'líquido',
  frio: 'refrigerada',
  geral: 'carga geral',
  veiculo: 'veículos',
  animais: 'animais',
};

export const TRAJETO = {
  kmPorDia: 600, // km rodados por dia (a pessoa muda)
  diasParaCarregar: 0.5,
  diasParaDescarregar: 0.5,
  parteDasCargas: 0.5, // das cargas que saem para lá, quantas o caminhão consegue (a disputa com os outros)
  esperaMaxima: 5, // dias esperando a carga, no máximo
  diasDaChance: 3, // a chance de achar a carga em até 3 dias
  vazioMaximo: 450, // km rodados vazios no fim, até o destino
  partidaKm: 150, // a carga pode estar numa região vizinha, até 150 km
  rotas: 5, // quantos trajetos mostrar
  cargasDaEmpresa: 2, // fretes da empresa no mesmo trecho para valer o R$/km dela
  fatorMinimo: 0.8,
  fatorMaximo: 2.5,
  avisoEnsacada: 0.2, // a partir de quanto da carga do trecho a tela avisa que pode ser a granel ou ensacada
};

export const PRODUTOS_ENSACAVEIS = 'arroz, açúcar, adubo, sal, cal ou gesso';
// As carrocerias que levam essas cargas de um jeito só (a granel ou ensacadas).
export const CARROCERIAS_DE_UM_JEITO = ['carga_seca', 'cacamba'];

// Frete de retorno (0.22.1): onde o frete costuma sair abaixo do piso (com
// documentação burlada), quanto do piso da ANTT conta, por região do país.
// A empresa muda; sem nada guardado, 70% no Norte e no Nordeste.
export const REGIOES_DO_PAIS = [['N', 'Norte'], ['NE', 'Nordeste'], ['CO', 'Centro-Oeste'], ['SE', 'Sudeste'], ['S', 'Sul']];
export const REGIAO_DA_UF = {
  AC: 'N', AM: 'N', AP: 'N', PA: 'N', RO: 'N', RR: 'N', TO: 'N',
  AL: 'NE', BA: 'NE', CE: 'NE', MA: 'NE', PB: 'NE', PE: 'NE', PI: 'NE', RN: 'NE', SE: 'NE',
  DF: 'CO', GO: 'CO', MS: 'CO', MT: 'CO',
  ES: 'SE', MG: 'SE', RJ: 'SE', SP: 'SE',
  PR: 'S', RS: 'S', SC: 'S',
};
export const FRETE_REGIAO_PADRAO = { N: 70, NE: 70, CO: null, SE: null, S: null };
export const nomeDaRegiaoDoPais = (sigla) => REGIOES_DO_PAIS.find(([s]) => s === sigla)?.[1] ?? null;

const ok = (v) => v !== null && v !== undefined && Number.isFinite(Number(v));
const media = (xs) => (xs.length > 0 ? xs.reduce((s, x) => s + Number(x), 0) / xs.length : null);
const limitar = (v, min, max) => Math.min(max, Math.max(min, v));
const reais = (v) => Math.round(v * 100) / 100;

// O frete por região que vale: o que a empresa guardou (raiox_custos) ou o padrão.
export function freteDaRegiao(guardado) {
  if (!guardado || typeof guardado !== 'object' || Array.isArray(guardado)) return { ...FRETE_REGIAO_PADRAO };
  const saida = {};
  for (const [sigla] of REGIOES_DO_PAIS) {
    const v = guardado[sigla];
    saida[sigla] = ok(v) && Number(v) > 0 ? Number(v) : null;
  }
  return saida;
}

export function mediana(valores) {
  const xs = valores.filter(ok).map(Number).sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const meio = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[meio] : (xs[meio - 1] + xs[meio]) / 2;
}

// Distância em linha reta (km), igual a app.km_entre() no banco (lib/geo.js).
export { kmEntre };

// Os grupos de carga que a carroceria leva (null = todos).
export const gruposDaCarroceria = (carroceria) => GRUPOS_DA_CARROCERIA[carroceria] ?? null;

// O tipo de carga do piso: o do grupo que mais sai no trecho (ou o primeiro da carroceria).
export function tipoDoFrete(grupo, carroceria) {
  const g = grupo ?? gruposDaCarroceria(carroceria)?.[0] ?? 'geral';
  if (g === 'ensacavel' && (carroceria === 'graneleira' || carroceria === 'cacamba')) return 'granel_solido';
  return TIPO_DO_GRUPO[g] ?? 'carga_geral';
}

// "2026-04-01" + 3 meses → "2026-07-01".
export function somarMeses(mes, n) {
  const [a, m] = mes.split('-').map(Number);
  const total = a * 12 + (m - 1) + n;
  return `${String(Math.floor(total / 12)).padStart(4, '0')}-${String((total % 12) + 1).padStart(2, '0')}-01`;
}

// Os 3 últimos meses e os 3 de antes (os meses que não vieram ficam de fora).
export function estatisticas(serie) {
  const valores = serie ?? [];
  const n = valores.length;
  const recente = media(valores.slice(Math.max(0, n - 3)).filter(ok));
  const antes = media(valores.slice(Math.max(0, n - 6), Math.max(0, n - 3)).filter(ok));
  const tendencia = recente !== null && antes ? recente / antes : null;
  return { recente, antes, tendencia };
}

export function textoDaTendencia(tendencia) {
  if (tendencia === null || tendencia === undefined) return null;
  if (tendencia >= 1.15) return 'subindo';
  if (tendencia <= 0.85) return 'caindo';
  return 'estável';
}

// Sazonalidade: quanto o mês da viagem costuma ter, comparado com os 3
// últimos meses, pelo que sai da região (só com 13 meses ou mais na série).
export function fatorDeSazonalidade(serie, { inicio, mes }) {
  const valores = serie ?? [];
  if (!inicio || !mes || valores.filter(ok).length < 13) return 1;
  const porMes = new Map();
  const todos = [];
  valores.forEach((v, i) => {
    if (!ok(v)) return;
    const m = Number(somarMeses(inicio, i).slice(5, 7));
    if (!porMes.has(m)) porMes.set(m, []);
    porMes.get(m).push(Number(v));
    todos.push(Number(v));
  });
  const geral = media(todos);
  if (!geral) return 1;
  const indice = (m) => (porMes.has(m) ? media(porMes.get(m)) / geral : 1);
  const ultimos = [0, 1, 2].map((k) => Number(somarMeses(inicio, valores.length - 1 - k).slice(5, 7)));
  const base = media(ultimos.map(indice));
  if (!base) return 1;
  return limitar(indice(Number(mes.slice(5, 7))) / base, 0.6, 1.6);
}

// A chance de achar a carga em até `dias` dias e a espera esperada, pelas
// cargas por mês (o caminhão consegue uma parte delas: a disputa com os outros).
export function chanceDaCarga(cargasPorMes, dias = TRAJETO.diasDaChance) {
  const porDia = (Math.max(0, Number(cargasPorMes) || 0) / 30) * TRAJETO.parteDasCargas;
  return 1 - Math.exp(-porDia * dias);
}

export function esperaPelaCarga(cargasPorMes) {
  const porDia = (Math.max(0, Number(cargasPorMes) || 0) / 30) * TRAJETO.parteDasCargas;
  return porDia > 0 ? Math.min(TRAJETO.esperaMaxima, 1 / porDia) : TRAJETO.esperaMaxima;
}

// Quanto a empresa recebe acima do piso da ANTT: a mediana de frete ÷ piso
// dos fretes do último ano (com 3 ou mais; senão, o piso).
export function fatorDoPiso(historico, { carroceria = null, eixos = null } = {}) {
  const razoes = [];
  for (const h of historico ?? []) {
    const piso = pisoMinimo({ tabela: 'A', tipo: tipoDoFrete(null, h.carroceria ?? carroceria), eixos: h.eixos ?? eixos, km: h.km });
    if (piso > 0 && Number(h.valor) > 0) razoes.push(Number(h.valor) / piso);
  }
  if (razoes.length < 3) return { fator: 1, cargas: razoes.length, fonte: 'piso' };
  return {
    fator: Math.round(limitar(mediana(razoes), TRAJETO.fatorMinimo, TRAJETO.fatorMaximo) * 100) / 100,
    cargas: razoes.length,
    fonte: 'empresa',
  };
}

// O R$/km que a empresa recebeu no trecho (região a região), com 2 fretes ou mais.
export function precoDaEmpresa(historico, o, d) {
  const lista = (historico ?? []).filter((h) => h.o === o && h.d === d && Number(h.km) > 0 && Number(h.valor) > 0);
  if (lista.length < TRAJETO.cargasDaEmpresa) return null;
  return { porKm: reais(mediana(lista.map((h) => Number(h.valor) / Number(h.km)))), cargas: lista.length };
}

// O frete esperado de um trecho: o R$/km da empresa (onde ela já rodou); a
// parte do piso da região de onde sai a carga (frete de retorno); ou o piso
// da ANTT × o fator da empresa. null sem piso para esse tipo e eixos.
export function precoDoTrecho({ km, grupo, carroceria, eixos, fator = 1, daEmpresa = null, pctDaRegiao = null, regiao = null }) {
  if (daEmpresa) return { valor: reais(daEmpresa.porKm * km), fonte: 'empresa', porKm: daEmpresa.porKm, cargas: daEmpresa.cargas };
  const tipo = tipoDoFrete(grupo, carroceria);
  const piso = pisoMinimo({ tabela: 'A', tipo, eixos, km }) ?? pisoMinimo({ tabela: 'A', tipo: 'carga_geral', eixos, km });
  if (!(piso > 0)) return null;
  if (ok(pctDaRegiao) && Number(pctDaRegiao) > 0) {
    return { valor: reais((piso * Number(pctDaRegiao)) / 100), fonte: 'regiao', piso, pct: Number(pctDaRegiao), regiao, tipo };
  }
  return { valor: reais(piso * fator), fonte: 'piso', piso, fator, tipo };
}

export const lugarDaRegiao = (r) => (r ? `${r.polo ?? r.nome}/${r.uf}` : '?');

// Os trajetos, do maior lucro por dia para o menor.
//   mapa: o que planejar_trajeto() devolve
//   custo: custoDosNumeros() do Raio-X (porKm e pctDoFrete)
//   mes: "AAAA-MM-01" da viagem (a sazonalidade)
//   freteRegiao: freteDaRegiao() (quanto do piso conta onde o frete de retorno sai abaixo dele)
//   esperaCarregar, esperaDescarregar: dias (o modo viagem aprende das viagens da empresa, 0.23.0)
export function planejar(mapa, {
  carroceria = null, eixos, custo, kmPorDia = TRAJETO.kmPorDia, mes = null, freteRegiao = FRETE_REGIAO_PADRAO,
  esperaCarregar = TRAJETO.diasParaCarregar, esperaDescarregar = TRAJETO.diasParaDescarregar,
}) {
  if (!mapa?.pronto || mapa.erro) return { rotas: [], erro: mapa?.erro ?? 'SEM_DADOS' };
  if (!custo?.porKm) return { rotas: [], erro: 'SEM_CUSTO' };
  const regioes = new Map((mapa.regioes ?? []).map((r) => [r.id, r]));
  const fator = fatorDoPiso(mapa.historico, { carroceria, eixos });
  const porKm = custo.porKm.diesel + custo.porKm.viagem + custo.porKm.reserva;
  const pct = custo.pctDoFrete ?? 0;
  const velocidade = kmPorDia > 0 ? kmPorDia : TRAJETO.kmPorDia;
  const inicio = mapa.serie?.inicio ?? null;
  const destino = mapa.destino ?? null;
  const raio = Number(mapa.raio_km) || 0;
  const maxTrechos = destino ? 3 : 2;
  const alvo = new Set(mapa.alvo ?? []);

  // Cada trecho (par de regiões), uma vez: distância, cargas por mês, chance, espera e preço.
  const saindo = new Map();
  const trechos = new Map();
  for (const t of mapa.trechos ?? []) {
    const de = regioes.get(t.o);
    const para = regioes.get(t.d);
    if (!de || !para) continue;
    const km = kmEstimado(kmEntre(de, para));
    const est = estatisticas(t.s);
    if (!km || !(est.recente > 0)) continue;
    const volume = est.recente * fatorDeSazonalidade(de.saidas, { inicio, mes });
    const sigla = REGIAO_DA_UF[de.uf] ?? null;
    const pctDaRegiao = sigla ? freteRegiao?.[sigla] ?? null : null;
    const preco = precoDoTrecho({
      km, grupo: t.grupo, carroceria, eixos, fator: fator.fator, daEmpresa: precoDaEmpresa(mapa.historico, t.o, t.d), pctDaRegiao, regiao: sigla,
    });
    if (!preco) continue;
    // Avisos: o frete de retorno abaixo do piso e a carga que pode sair a granel ou ensacada
    const abaixoDoPiso = ok(pctDaRegiao) && Number(pctDaRegiao) < 100 ? { pct: Number(pctDaRegiao), regiao: nomeDaRegiaoDoPais(sigla) } : null;
    const parteEnsacada = CARROCERIAS_DE_UM_JEITO.includes(carroceria) && Number(t.ens) > 0 ? Math.min(1, Number(t.ens) / est.recente) : 0;
    const info = {
      o: t.o,
      d: t.d,
      km,
      volume: Math.round(volume * 10) / 10,
      tendencia: textoDaTendencia(est.tendencia),
      chance: chanceDaCarga(volume),
      espera: esperaPelaCarga(volume),
      grupo: t.grupo ?? null,
      contratantes: Number(t.contratantes) || 0,
      preco,
      abaixoDoPiso,
      ensacada: parteEnsacada >= TRAJETO.avisoEnsacada ? Math.round(parteEnsacada * 100) / 100 : 0,
    };
    trechos.set(`${t.o}>${t.d}`, info);
    if (!saindo.has(t.o)) saindo.set(t.o, []);
    saindo.get(t.o).push(info);
  }

  // Até a primeira carga: a distância da cidade até o polo da região (ou o
  // vazio médio dentro dela, o que for maior).
  const vazioAteACarga = (id) => {
    const r = regioes.get(id);
    const ate = mapa.origem ? Math.round(kmEntre(mapa.origem, r) * FATOR_DA_ESTRADA) : 0;
    return Math.max(Number(r.km_interno) || 0, ate);
  };
  // Do fim da última entrega até perto do destino (dentro do raio, nada).
  const vazioAteODestino = (id) => {
    if (!destino) return 0;
    const linha = kmEntre(regioes.get(id), destino);
    return linha <= raio ? 0 : Math.round((linha - raio) * FATOR_DA_ESTRADA);
  };

  const rotas = [];
  const avaliar = (caminho) => {
    const final = vazioAteODestino(caminho.at(-1));
    if (final > TRAJETO.vazioMaximo) return;
    let dias = 0;
    let receita = 0;
    let custoTotal = 0;
    let lucroEsperado = 0;
    let lucroSePegar = 0;
    let kmCarregado = 0;
    let kmVazio = 0;
    let menorChance = 1;
    const partes = [];
    for (let i = 0; i < caminho.length - 1; i++) {
      const t = trechos.get(`${caminho[i]}>${caminho[i + 1]}`);
      const vazio = i === 0 ? vazioAteACarga(t.o) : Number(regioes.get(t.o).km_interno) || 0;
      const custoCarregado = t.km * porKm + pct * t.preco.valor;
      const custoVazio = vazio * porKm;
      const lucro = t.preco.valor - custoCarregado - custoVazio;
      const esperado = t.chance * (t.preco.valor - custoCarregado) - custoVazio;
      const diasDoTrecho = t.espera + esperaCarregar + (t.km + vazio) / velocidade + esperaDescarregar;
      dias += diasDoTrecho;
      receita += t.preco.valor;
      custoTotal += custoCarregado + custoVazio;
      lucroEsperado += esperado;
      lucroSePegar += lucro;
      kmCarregado += t.km;
      kmVazio += vazio;
      menorChance = Math.min(menorChance, t.chance);
      partes.push({ ...t, de: lugarDaRegiao(regioes.get(t.o)), para: lugarDaRegiao(regioes.get(t.d)), kmVazio: vazio, lucro: reais(lucro), dias: diasDoTrecho });
    }
    const custoFinal = final * porKm;
    const abaixoDoPiso = partes.some((p) => p.abaixoDoPiso);
    let risco = menorChance >= 0.85 ? 'baixo' : menorChance >= 0.5 ? 'médio' : 'alto';
    // Frete de retorno abaixo do piso (documentação burlada): risco pelo menos médio
    if (abaixoDoPiso && risco === 'baixo') risco = 'médio';
    dias += final / velocidade;
    custoTotal += custoFinal;
    lucroEsperado -= custoFinal;
    lucroSePegar -= custoFinal;
    kmVazio += final;
    rotas.push({
      caminho,
      nomes: caminho.map((id) => lugarDaRegiao(regioes.get(id))),
      trechos: partes,
      vazioFinal: final,
      kmCarregado,
      kmVazio,
      dias: Math.round(dias * 10) / 10,
      receita: reais(receita),
      custo: reais(custoTotal),
      lucroEsperado: reais(lucroEsperado),
      lucroSePegar: reais(lucroSePegar),
      lucroPorDia: reais(lucroEsperado / dias),
      chance: menorChance,
      risco,
      abaixoDoPiso,
      ensacada: partes.some((p) => p.ensacada > 0),
      direta: caminho.length === 2 && alvo.has(caminho[1]),
    });
  };
  const andar = (caminho) => {
    if (caminho.length > 1) avaliar(caminho);
    if (caminho.length - 1 >= maxTrechos) return;
    for (const t of saindo.get(caminho.at(-1)) ?? []) {
      if (!caminho.includes(t.d)) andar([...caminho, t.d]);
    }
  };
  for (const p of mapa.partida ?? []) if (regioes.has(p)) andar([p]);

  rotas.sort((a, b) => b.lucroPorDia - a.lucroPorDia || a.dias - b.dias);
  const melhor = rotas[0]?.lucroPorDia ?? 0;
  const comNota = (r) => ({ ...r, nota: melhor > 0 ? Math.max(0, Math.round((100 * r.lucroPorDia) / melhor)) : 0 });
  const escolhidas = rotas.slice(0, TRAJETO.rotas).map(comNota);
  // A carga direta até o destino, se houver, sempre aparece (para comparar).
  const direta = rotas.find((r) => r.direta);
  if (direta && !escolhidas.some((r) => r.direta)) escolhidas.push(comNota(direta));
  // Para comparar: ir vazio direto até o destino.
  let vazioDireto = null;
  if (destino && mapa.origem) {
    const linha = kmEntre(mapa.origem, destino);
    const km = linha <= raio ? 0 : Math.round((linha - raio) * FATOR_DA_ESTRADA);
    vazioDireto = { km, custo: reais(km * porKm), dias: Math.round((km / velocidade) * 10) / 10 };
  }
  return { rotas: escolhidas, fator, vazioDireto, porKm: reais(porKm), pct, mes, freteRegiao, esperaCarregar, esperaDescarregar };
}

// "Via Campinas/SP" ou "Direto para Joinville/SC" (o trajeto recomendado).
export function textoDaRecomendacao(rota) {
  if (!rota) return null;
  const meio = rota.nomes.slice(1, -1);
  if (meio.length === 0) return `Direto para ${rota.nomes.at(-1)}`;
  return `Via ${meio.join(' e ')}`;
}
